import { useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCars } from '../context/CarsContext';
import { generateCommands } from '../services/commandGenerator';
import { browserControlWS } from '../services/browserControlWebSocket';
import { config } from '../config/runtimeConfig';
import { useWidgetMode } from '../context/WidgetModeContext';
import { resolveWebsitePage } from '../utils/pageContextParser';

/**
 * React hook для генерации и обновления высокоуровневых команд
 *
 * Автоматически генерирует список доступных команд на основе:
 * - Текущего маршрута (location.pathname)
 * - Списка автомобилей из API (useCars)
 * - Текущей серверной страницы (Issue #741)
 * - Активных фильтров (опционально)
 *
 * При изменении любого из этих параметров:
 * 1. Генерируется новый список команд
 * 2. Список отправляется в WebSocket сервер через browserControlWS
 *
 * @param {Object} [options]
 * @param {boolean} [options.pinned] - Чат приколот к странице: команды,
 *   уводящие с неё, агенту не выдаются (#1330).
 * @returns {Array} Массив команд с полями {id, description}
 */
export function useCommands({ pinned = false } = {}) {
  const { getAllCars, loading } = useCars();
  const location = useLocation();
  // #828: читаем widget mode через per-instance React Context (не из глобального
  // window.runtimeConfig), иначе в multi-embed setup'е (chat-only + mini-catalog)
  // command generation пользовалась чужим mode из-за race condition.
  const widgetMode = useWidgetMode();

  // Генерируем команды при изменении маршрута или списка автомобилей
  const commands = useMemo(() => {
    // Генерируем навигационные команды даже во время загрузки (с пустым cars)
    // Полный набор команд (с фильтрами и view_cars) добавится после загрузки
    const cars = loading ? [] : getAllCars();

    // Для HashRouter используем pathname из location (React Router автоматически парсит hash)
    // Например: hash = "#/cars" -> location.pathname = "/cars"
    const currentRoute = location.pathname;

    // Получаем websitePages из конфига (widgetMode теперь из Context, см. выше)
    const websitePages = config.WEBSITE_PAGES || [];

    // Issue #741: Определяем текущую серверную страницу для ограничения каталожных команд
    const websitePage = resolveWebsitePage();

    console.log('[useCommands] Generating commands for route:', currentRoute, 'with', cars.length, 'cars, websitePages:', websitePages.length, 'mode:', widgetMode, 'currentServerPage:', websitePage?.id || 'none');

    const generatedCommands = generateCommands(cars, currentRoute, { websitePages, widgetMode, websitePage, pinned });

    console.log('[useCommands] Generated', generatedCommands.length, 'commands');

    return generatedCommands;
  }, [getAllCars, loading, location.pathname, widgetMode, pinned]);

  // Обновляем команды в WebSocket при их изменении
  useEffect(() => {
    // Пустой набор тоже отправляем, если он стал пустым осознанно (`pinned`):
    // иначе на приколотой странице у агента остался бы предыдущий набор и он
    // продолжал бы уводить со страницы. Условие `length > 0` осталось для
    // обычных страниц — там пустота означает «команды ещё не готовы».
    if (commands.length > 0 || pinned) {
      console.log('[useCommands] Updating commands in browserControlWS');
      browserControlWS.updateCommands(commands);
    }
  }, [commands, pinned]);

  return commands;
}

export default useCommands;
